import settings from 'settings'
import { AGGREGATION_TYPES } from '@carto/kepler.gl/constants'
import { GroupDateType } from '@carto/api-client'
import { TIME_SERIES_CHART_TYPES } from '@carto/meridian-ds/widgets'
import { KeplerAggregationType, KeplerFieldType, LayerType, LayerTypes } from '../types'

import { ProviderType } from 'features/connections'
import { BasemapSettings } from 'features/account/types'

export const BUILDER_TITLE = 'CARTO Builder'

export const TOOLBAR_HEIGHT = '49px'
export const SIDEBAR_WIDTH = '320px'
export const SQL_PANEL_HEIGHT = '320px'
export const SQL_PANEL_MINIMIZED_HEIGHT = '48px'

// Our minimal discrete zoom level is 1 and we use it for data.
// However, for views it is convienient (and we always supported) zoom levels "down"-to 0.5, so we need to define
// additional fractional zoom level for deck.
// We use Math.round(deckZoom) for data, so we're ok with "real-fractional" minZoom 0.5
export const BUILDER_MIN_FRACTIONAL_ZOOM = 0.5
export const BUILDER_MIN_ZOOM_DISCRETE = Math.round(BUILDER_MIN_FRACTIONAL_ZOOM)

export const BUILDER_MAX_ZOOM = 21
export const BUILDER_MAX_ZOOM_MAPLIBRE = 24

export const NEW_MAP_STATE = {
  latitude: 0,
  longitude: 1.2337799069999988,
  zoom: 0.7296207435530385,
  bearing: 0,
  pitch: 0,
  minZoom: BUILDER_MIN_FRACTIONAL_ZOOM
}

export enum ArtificialSchemaField {
  POINT_DENSITY = '_carto_point_density',
  FEATURE_ID = '_carto_feature_id'
}

// Despite our Workflow for generating Static Tilesets doesn't include the geoid column, we recommend using it, so it sounds like a good fallback
export const TILESETS_UNIQUE_ID_PROPERTY = 'geoid'
// _carto_feature_id is a column calculated by the Maps API during runtime, and represents the unique identifier of each feature
export const DEFAULT_UNIQUE_ID_PROPERTY = ArtificialSchemaField.FEATURE_ID

export const MAP_CHUNK_SIZE = 1000
export const KEPLER_MAP_CONFIG_VERSION = 'v1'

export const TILEJSON_FORMAT_TYPES: LayerType[] = [
  LayerTypes.Tileset,
  LayerTypes.Quadbin,
  LayerTypes.H3,
  LayerTypes.HeatmapTile,
  LayerTypes.ClusterTile
]
export const RANGE_FILTER_TYPE = 'range'
export const MULTISELECT_FILTER_TYPE = 'multiSelect'
export const DEFAULT_GEOM_COLUMN = 'geom'
export const GEOM_COLUMNS_LIST = ['geom', 'geog', 'the_geom', 'geo', 'geometry']

export const UNSUPPORTED_HIGHLIGHT_LAYER_TYPES: LayerType[] = [
  LayerTypes.HeatmapTile,
  LayerTypes.ClusterTile,
  LayerTypes.Raster
]

export const MAX_HOVER_FIELDS = 5

// Thumbnails
export const THUMBNAILS_MIN_MAP_WIDTH = 500
export const THUMBNAILS_RESOLUTION = 'ONE_X'
export const THUMBNAILS_RATIO = 'SIXTEEN_BY_NINE'
// waiting for the layes to be ready interval
export const THUMBNAILS_INTERVAL_MILLISECONDS = 2000
export const THUMBNAILS_INTERVAL_MAX_RETRIES = 10
export const DEFAULT_LOAD_MAP_OPTIONS = { autoCreateLayers: false, centerMap: false, keepExistingConfig: false }

export const FIELD_TYPES_NUMERIC: KeplerFieldType[] = ['integer', 'real']

export const CALCULATE_FEATURES_DEBOUNCE_TIME = 250

export const DEFAULT_BUCKETS_FOR_HISTOGRAM_WIDGET = 30
export const DEFAULT_STEP_SIZE_FOR_TIME_SERIES_WIDGET: GroupDateType = 'day'
export const DEFAULT_CHART_TYPE_FOR_TIME_SERIES_WIDGET = TIME_SERIES_CHART_TYPES.LINE

export const QUERY_TOO_LONG_TIME = 30000
export const TILES_TOO_LONG_TIME = 10000

export const columnPlaceHolder = '###column###'

const COMMON_AGGREGATION_TYPES = {
  [AGGREGATION_TYPES.count]: `count(${columnPlaceHolder})`,
  [AGGREGATION_TYPES.average]: `avg(${columnPlaceHolder})`,
  [AGGREGATION_TYPES.maximum]: `max(${columnPlaceHolder})`,
  [AGGREGATION_TYPES.minimum]: `min(${columnPlaceHolder})`,
  [AGGREGATION_TYPES.stdev]: `stddev(${columnPlaceHolder})`,
  [AGGREGATION_TYPES.sum]: `sum(${columnPlaceHolder})`,
  [AGGREGATION_TYPES.variance]: `variance(${columnPlaceHolder})`
}

export const AGGREGATION_TYPES_NUMERIC: KeplerAggregationType[] = [
  'sum',
  'count',
  'average',
  'maximum',
  'minimum',
  'stdev',
  'variance'
]

export const AGGREGATION_TYPES_STRING: KeplerAggregationType[] = ['mode', 'any_value']

export const AGGREGATION_TYPES_TO_SQL = {
  [ProviderType.BIGQUERY]: {
    ...COMMON_AGGREGATION_TYPES,
    [AGGREGATION_TYPES.mode]: `APPROX_TOP_COUNT(${columnPlaceHolder}, 1)[OFFSET(0)].value`,
    [AGGREGATION_TYPES.anyValue]: `ANY_VALUE(${columnPlaceHolder})`
  },
  [ProviderType.SNOWFLAKE]: {
    ...COMMON_AGGREGATION_TYPES,
    [AGGREGATION_TYPES.mode]: `MODE(${columnPlaceHolder})`,
    [AGGREGATION_TYPES.anyValue]: `ANY_VALUE(${columnPlaceHolder})`
  },
  [ProviderType.REDSHIFT]: {
    ...COMMON_AGGREGATION_TYPES,
    // We don't have support for MODE in redshift so the UI must not offer
    [AGGREGATION_TYPES.anyValue]: `ANY_VALUE(${columnPlaceHolder})`
  },
  [ProviderType.POSTGRES]: {
    ...COMMON_AGGREGATION_TYPES,
    [AGGREGATION_TYPES.mode]: `mode() WITHIN GROUP (ORDER BY ${columnPlaceHolder})`,
    // Postgres doesn't have ANY_VALUE function so we use MAX
    [AGGREGATION_TYPES.anyValue]: `MAX(${columnPlaceHolder})`
  },
  [ProviderType.DATABRICKS]: {
    ...COMMON_AGGREGATION_TYPES,
    [AGGREGATION_TYPES.mode]: `mode(${columnPlaceHolder})`,
    [AGGREGATION_TYPES.anyValue]: `ANY_VALUE(${columnPlaceHolder})`
  },
  [ProviderType.DATABRICKS_REST]: {
    ...COMMON_AGGREGATION_TYPES,
    [AGGREGATION_TYPES.mode]: `mode(${columnPlaceHolder})`,
    [AGGREGATION_TYPES.anyValue]: `ANY_VALUE(${columnPlaceHolder})`
  },
  [ProviderType.DATABRICKS_WAREHOUSE]: {
    ...COMMON_AGGREGATION_TYPES,
    [AGGREGATION_TYPES.mode]: `mode(${columnPlaceHolder})`,
    [AGGREGATION_TYPES.anyValue]: `ANY_VALUE(${columnPlaceHolder})`
  }
}

// IMPORTANT! This could collide with the column name in the schema, so we need to make sure that the alias is unique
export const DEFAULT_AGGREGATION_EXP_ALIAS = '__aggregationValue'
export const DEFAULT_AGGREGATION_EXP = `1 AS ${DEFAULT_AGGREGATION_EXP_ALIAS}`

export const DEFAULT_GEO_COLUMN = `${DEFAULT_GEOM_COLUMN}`

export const DEFAULT_AGGREGATION_RES_LEVEL_H3 = 4
export const DEFAULT_AGGREGATION_RES_LEVEL_QUADBIN = 6
export const DEFAULT_AGGREGATION_LEVEL_HEATMAP = 8
export const DEFAULT_AGGREGATION_LEVEL_CLUSTER = 8

export const DEFAULT_TILE_RESOLUTION = 0.5
export const DEFAULT_TILE_SIZE = 512
export const DEFAULT_TILE_DEBOUNCE_MS = 0

export const REDUCED_QUERIES_TILE_RESOLUTION = 1
export const REDUCED_QUERIES_TILE_SIZE = 1024
export const REDUCED_QUERIES_H3_TILE_SIZE = 1448
export const REDUCED_QUERIES_TILE_DEBOUNCE_MS = 100

export const QUADBIN_RESOLUTION = { min: 1, max: 9, default: DEFAULT_AGGREGATION_RES_LEVEL_QUADBIN }
export const H3_RESOLUTION = { min: 1, max: 6, default: DEFAULT_AGGREGATION_RES_LEVEL_H3 }

export const DEFAULT_COLOR_STEPS = 6
export const MIN_COLOR_STEPS = 3
export const MAX_COLOR_STEPS = 20

export const defaultBasemapsSettings: BasemapSettings = {
  carto: true,
  google: Boolean(settings.googleMapsApiKey),
  custom: false
}

export const LAYER_SIDE_PANEL = 'layer'
export const FILTER_SIDE_PANEL = 'filter'
export const LEGEND_SIDE_PANEL = 'legend'
export const AI_SIDE_PANEL = 'ai'

export const DEFAULT_DATA_SOURCE_COLOR = '#fff'

// NOTICE: Field names are case sensitive, and different providers may return
// different capitalizations for different fields. Before adding fields to this
// list, check that (a) providers are consistent, or (b) all cases are handled.
export const WIDGET_FIELDS_DENYLIST = new Set<string>([
  ArtificialSchemaField.POINT_DENSITY,
  ArtificialSchemaField.FEATURE_ID
])
export const POPUP_FIELDS_DENYLIST = new Set<string>([
  ArtificialSchemaField.POINT_DENSITY,
  ArtificialSchemaField.FEATURE_ID
])
export const ANALYSIS_FIELDS_DENYLIST = new Set<string>([
  ArtificialSchemaField.POINT_DENSITY,
  ArtificialSchemaField.FEATURE_ID
])
export const AGGREGATION_FIELDS_DENYLIST = new Set<string>([
  ArtificialSchemaField.POINT_DENSITY,
  ArtificialSchemaField.FEATURE_ID
])
export const AGENT_FIELDS_DENYLIST = new Set<string>([
  ArtificialSchemaField.POINT_DENSITY,
  ArtificialSchemaField.FEATURE_ID
])
